import React from 'react';

import clsx from 'clsx';
import { Box, ButtonBase, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { FiFileText } from 'react-icons/fi';

import { bytesToSize } from '../../../utils/fileSize';
import { ChatBubbleSide } from './ChatBubble';

interface Props {
  side: ChatBubbleSide;
  name: string;
  size?: number;
  onClick?: () => void;
}

// A file inside a bubble: icon, name and size, opened on click.
export const ChatFileTile: React.FC<Props> = ({ side, name, size, onClick }) => {
  const classes = useStyles();

  return (
    <ButtonBase
      component="div"
      className={clsx(classes.root, side === 'out' ? classes.out : classes.in)}
      onClick={onClick}
      disabled={!onClick}
      aria-label={onClick ? `Open ${name}` : name}
    >
      <Box className={classes.icon}>
        <FiFileText size={20} />
      </Box>
      <Box minWidth={0}>
        <Typography component="span" className={classes.name}>
          {name}
        </Typography>
        {!!size && (
          <Typography component="span" className={classes.size}>
            {bytesToSize(size)}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minWidth: 200,
      maxWidth: 320,
      padding: '6px 10px 6px 6px',
      borderRadius: 12,
      textAlign: 'left',
    },
    in: {
      '& $icon': { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white },
      '& $size': { color: theme.palette.text.secondary },
    },
    out: {
      color: theme.palette.common.white,
      '& $icon': { backgroundColor: alpha(theme.palette.common.white, 0.2) },
      '& $size': { color: alpha(theme.palette.common.white, 0.75) },
    },
    icon: {
      flex: 'none',
      width: 40,
      height: 40,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    name: {
      display: 'block',
      fontSize: 14,
      fontWeight: 500,
      lineHeight: '20px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    size: { display: 'block', fontSize: 12, lineHeight: '16px' },
  })
);
